import {
  useGetMeetingsQuery,
  useLazyGetMeetingAgreementsQuery,
  useProjectId,
} from '@consigli/hooks';
import { type MeetingAgreement } from '@consigli/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMsalAuth } from './use-msal-auth';
import { useTranscriptSource } from './use-transcript-source';

import type { TranscriptSourceMeeting } from '../components/transcript-source-modal';
import type { ParsedTranscriptEntry } from '../utils/export-types';

export interface AgreementSourceRef {
  agreementId: string;
  sourceSegmentIds?: string[] | null;
  meetingId?: string | null;
  meetingDatetime?: string | null;
}

interface CurrentMeeting {
  id: string;
  title?: string | null;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
  location?: string | null;
}

export interface UseAgreementTranscriptSourceResult {
  sourceRef: AgreementSourceRef | null;
  segmentIds: string[];
  entries: ParsedTranscriptEntry[];
  meeting: TranscriptSourceMeeting | undefined;
  isLoading: boolean;
  hasError: boolean;
  hasTranscript: boolean;
  showSource: (ref: AgreementSourceRef) => void;
  closeSource: () => void;
  retry: () => void;
}

const EMPTY_ENTRIES: ParsedTranscriptEntry[] = [];

const sameMoment = (a?: string | null, b?: string | null) => {
  if (!a || !b) return false;
  const left = Date.parse(a);
  const right = Date.parse(b);
  return !Number.isNaN(left) && left === right;
};

export const useAgreementTranscriptSource = (
  currentMeeting: CurrentMeeting | undefined,
  agreements: MeetingAgreement[],
  transcriptEntries?: ParsedTranscriptEntry[],
): UseAgreementTranscriptSourceResult => {
  const projectId = useProjectId();
  const { acquireToken, userEmail, userId } = useMsalAuth();
  const [getAgreements] = useLazyGetMeetingAgreementsQuery();
  const transcript = useTranscriptSource();

  const [sourceRef, setSourceRef] = useState<AgreementSourceRef | null>(null);
  const [segmentsByMeeting, setSegmentsByMeeting] = useState<
    Record<string, Record<string, string[]>>
  >({});
  const [failedMeetingIds, setFailedMeetingIds] = useState<Record<string, true>>({});
  const [loadingMeetingId, setLoadingMeetingId] = useState<string | null>(null);
  const inFlightRef = useRef<string | null>(null);

  const { data: meetingsResponse } = useGetMeetingsQuery({ projectId }, { skip: !projectId });

  const meetings = useMemo(
    () => (Array.isArray(meetingsResponse) ? meetingsResponse : (meetingsResponse?.meetings ?? [])),
    [meetingsResponse],
  );

  const byId = useMemo(
    () => new Map(agreements.map((agreement) => [agreement.id, agreement])),
    [agreements],
  );

  const resolveMeetingId = useCallback(
    (ref: AgreementSourceRef): string | undefined => {
      if (ref.meetingId) return ref.meetingId;

      const known = byId.get(ref.agreementId);
      if (known?.meetingId) return known.meetingId;

      const matches = meetings.filter((meeting) =>
        sameMoment(meeting.scheduledStartTime, ref.meetingDatetime),
      );
      if (matches.length === 1) return matches[0].id;

      return currentMeeting?.id;
    },
    [byId, meetings, currentMeeting?.id],
  );

  const activeMeetingId = sourceRef ? resolveMeetingId(sourceRef) : undefined;
  const isCurrentMeeting = !!activeMeetingId && activeMeetingId === currentMeeting?.id;

  const loadAgreementsOf = useCallback(
    async (meetingId: string) => {
      inFlightRef.current = meetingId;
      setLoadingMeetingId(meetingId);
      try {
        const msAccessToken = await acquireToken();
        if (!msAccessToken) throw new Error('Failed to acquire MS token');

        const fetched = await getAgreements({
          meetingId,
          projectId,
          msAccessToken,
          userEmail,
          userId,
        }).unwrap();

        setSegmentsByMeeting((prev) => ({
          ...prev,
          [meetingId]: Object.fromEntries(
            (fetched ?? []).map((agreement) => [agreement.id, agreement.sourceSegmentIds ?? []]),
          ),
        }));
      } catch (error) {
        console.error(`Failed to load agreements of meeting ${meetingId}:`, error);
        setFailedMeetingIds((prev) => ({ ...prev, [meetingId]: true }));
      } finally {
        inFlightRef.current = null;
        setLoadingMeetingId((current) => (current === meetingId ? null : current));
      }
    },
    [acquireToken, getAgreements, projectId, userEmail, userId],
  );

  const needsSegments =
    !!sourceRef &&
    !sourceRef.sourceSegmentIds?.length &&
    !byId.has(sourceRef.agreementId) &&
    !!activeMeetingId;

  useEffect(() => {
    if (!needsSegments || !activeMeetingId) return;
    if (segmentsByMeeting[activeMeetingId] || failedMeetingIds[activeMeetingId]) return;
    if (inFlightRef.current) return;

    void loadAgreementsOf(activeMeetingId);
  }, [needsSegments, activeMeetingId, segmentsByMeeting, failedMeetingIds, loadAgreementsOf]);

  const segmentIds = useMemo(() => {
    if (!sourceRef) return [];
    if (sourceRef.sourceSegmentIds?.length) return sourceRef.sourceSegmentIds;

    const known = byId.get(sourceRef.agreementId)?.sourceSegmentIds;
    if (known?.length) return known;

    if (!activeMeetingId) return [];
    return segmentsByMeeting[activeMeetingId]?.[sourceRef.agreementId] ?? [];
  }, [sourceRef, byId, activeMeetingId, segmentsByMeeting]);

  const clearFailure = useCallback((meetingId: string) => {
    setFailedMeetingIds((prev) => {
      if (!prev[meetingId]) return prev;
      const next = { ...prev };
      delete next[meetingId];
      return next;
    });
  }, []);

  const showSource = useCallback(
    (ref: AgreementSourceRef) => {
      setSourceRef(ref);

      const meetingId = resolveMeetingId(ref);
      if (meetingId) clearFailure(meetingId);

      if (meetingId && meetingId !== currentMeeting?.id) {
        transcript.showSource({ meetingId, sourceSegmentIds: ref.sourceSegmentIds });
      } else {
        transcript.closeSource();
      }
    },
    [resolveMeetingId, currentMeeting?.id, transcript, clearFailure],
  );

  const closeSource = useCallback(() => {
    setSourceRef(null);
    transcript.closeSource();
  }, [transcript]);

  const retry = useCallback(() => {
    if (activeMeetingId) clearFailure(activeMeetingId);
    transcript.retry();
  }, [activeMeetingId, clearFailure, transcript]);

  const isSegmentsLoading = !!activeMeetingId && loadingMeetingId === activeMeetingId;
  const hasSegmentsError = !!activeMeetingId && !!failedMeetingIds[activeMeetingId];

  return {
    sourceRef,
    segmentIds,
    entries: isCurrentMeeting ? (transcriptEntries ?? EMPTY_ENTRIES) : transcript.entries,
    meeting: isCurrentMeeting ? currentMeeting : transcript.meeting,
    isLoading: isCurrentMeeting ? isSegmentsLoading : transcript.isLoading || isSegmentsLoading,
    hasError: isCurrentMeeting ? hasSegmentsError : transcript.hasError || hasSegmentsError,
    hasTranscript: !!transcriptEntries?.length,
    showSource,
    closeSource,
    retry,
  };
};
